// src/modules/competitions/components/VideoTypeDetailModal.tsx

import React, { useState, useEffect } from 'react';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import VideoComparator from './VideoComparator';
import CompactVideoUploader from './CompactVideoUploader';
import { CompetitionVideoType, CompetitionVideoSubmission, ExampleVideo } from '../types';
import { videoTypesService } from '../services/videoTypesService';
import '../styles/videoTypeModal.css';

interface Props {
    videoType: CompetitionVideoType;
    participantId?: number;
    existingSubmission?: CompetitionVideoSubmission;
    categoryRules?: string | null;
    onClose: () => void;
    onUploadSuccess: () => void;
}

type TabType = 'video' | 'details' | 'criteria' | 'rules';

const VideoTypeDetailModal: React.FC<Props> = ({
    videoType,
    participantId,
    existingSubmission,
    categoryRules,
    onClose,
    onUploadSuccess
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('video');
    const [examples, setExamples] = useState<ExampleVideo[]>([]);
    const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
    const [criteria, setCriteria] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [videoDuration, setVideoDuration] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    
    // 🆕 Estados para URLs con tokens temporales
    const [exampleVideoUrl, setExampleVideoUrl] = useState<string | null>(null);
    const [participantVideoUrl, setParticipantVideoUrl] = useState<string | null>(null);
    const [loadingVideos, setLoadingVideos] = useState(false);

    useEffect(() => {
        loadData();
    }, [videoType.id]);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    // 🆕 Cargar URLs con tokens cuando cambian los videos
    useEffect(() => {
        if (activeTab === 'video') {
            loadVideoUrls();
        }
    }, [activeTab, currentExampleIndex, existingSubmission]);

    const loadData = async () => {
        try {
            setIsLoading(true);

            // Cargar ejemplos
            const examplesRes = await videoTypesService.getExampleVideos(videoType.id);
            if (examplesRes.success && examplesRes.data) {
                setExamples(examplesRes.data.examples);
            }

            // Cargar criterios
            const criteriaRes = await videoTypesService.getCriteriaByVideoType(videoType.id);
            if (criteriaRes.success && criteriaRes.data) {
                setCriteria(criteriaRes.data.criteria);
            }
        } catch (err) {
            console.error('Error loading modal data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // 🆕 Cargar URLs de video con tokens temporales
    const loadVideoUrls = async () => {
        if (!participantId) return;

        try {
            setLoadingVideos(true);
            
            // Cargar URL del video ejemplo actual
            const currentExample = examples[currentExampleIndex];
            if (currentExample) {
                const exampleUrl = await videoTypesService.getExampleVideoUrlWithToken(
                    currentExample.id,
                    participantId,
                    videoType.id
                );
                setExampleVideoUrl(exampleUrl);
            }

            // Cargar URL del video del participante si existe
            if (existingSubmission) {
                const partUrl = await videoTypesService.getParticipantVideoUrlWithToken(
                    participantId,
                    videoType.id
                );
                setParticipantVideoUrl(partUrl);
            }
        } catch (err) {
            console.error('Error cargando URLs de video:', err);
            setError('Error al cargar videos');
        } finally {
            setLoadingVideos(false);
        }
    };

    const handleFileSelect = (file: File, url: string, duration: number) => {
        setSelectedFile(file);
        setPreviewUrl(url);
        setVideoDuration(duration);
        setError(null);
    };

    const handleUpload = async () => {
        if (!selectedFile || !participantId) return;

        try {
            setIsUploading(true);
            setError(null);

            const formData = new FormData();
            formData.append('video', selectedFile);
            formData.append('participant_id', participantId.toString());
            formData.append('video_type_id', videoType.id.toString());

            const response = await videoTypesService.submitVideo(formData);

            if (response.success) {
                // Limpiar cache de tokens al subir nuevo video
                videoTypesService.clearTokenCache();
                onUploadSuccess();
                onClose();
            } else {
                setError(response.message || 'Error al subir video');
            }
        } catch (err) {
            setError('Error al subir video');
        } finally {
            setIsUploading(false);
        }
    };

    const handleCancelUpload = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setSelectedFile(null);
        setPreviewUrl(null);
        setVideoDuration(0);
        setError(null);
    };

    const handleDelete = async () => {
        if (!existingSubmission || !participantId) return;

        const confirmed = window.confirm('¿Estás seguro de eliminar este video?');
        if (!confirmed) return;

        try {
            setIsDeleting(true);
            setError(null);

            const response = await videoTypesService.deleteVideo(
                participantId,
                videoType.id
            );

            if (response.success) {
                // Limpiar cache de tokens al eliminar video
                videoTypesService.clearTokenCache();
                onUploadSuccess();
                onClose();
            } else {
                setError(response.message || 'Error al eliminar video');
            }
        } catch (err) {
            setError('Error al eliminar video');
        } finally {
            setIsDeleting(false);
        }
    };

    const nextExample = () => {
        setCurrentExampleIndex((prev) => (prev + 1) % examples.length);
    };

    const prevExample = () => {
        setCurrentExampleIndex((prev) => (prev - 1 + examples.length) % examples.length);
    };

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const renderVideoTab = () => {
        const exampleVideo = examples[currentExampleIndex];
        const hasSubmission = !!existingSubmission;
        const isUploadingNew = !!selectedFile;

        // Mostrar loading mientras se cargan las URLs
        if (loadingVideos && (exampleVideo || hasSubmission)) {
            return (
                <div className="tab-content-video">
                    <Loading message="Cargando videos..." />
                </div>
            );
        }

        // CASO 1: Usuario YA subió video
        if (hasSubmission && !isUploadingNew) {
            // Si hay ejemplo: mostrar comparador
            if (exampleVideo && exampleVideoUrl && participantVideoUrl) {
                return (
                    <div className="tab-content-video">
                        <VideoComparator
                            exampleVideo={exampleVideoUrl}
                            participantVideo={participantVideoUrl}
                            exampleTitle={exampleVideo.title || undefined}
                            isLoading={loadingVideos}
                        />
                        {participantId && (
                            <div className="video-actions">
                                <Button
                                    onClick={handleDelete}
                                    variant="danger"
                                    disabled={isDeleting}
                                    className="btn-delete"
                                >
                                    {isDeleting ? 'Eliminando...' : '🗑️ Eliminar y subir nuevo'}
                                </Button>
                            </div>
                        )}
                    </div>
                );
            }

            // Sin ejemplo: mostrar solo el video del usuario
            if (participantVideoUrl) {
                return (
                    <div className="tab-content-video">
                        <div className="single-video-view">
                            <div className="example-video-container">
                                <span className="video-label-single">TU VIDEO</span>
                                <video
                                    src={participantVideoUrl}
                                    controls
                                    className="single-video"
                                />
                            </div>
                        </div>

                        {participantId && (
                            <div className="video-actions">
                                <Button
                                    onClick={handleDelete}
                                    variant="danger"
                                    disabled={isDeleting}
                                    className="btn-delete"
                                >
                                    {isDeleting ? 'Eliminando...' : '🗑️ Eliminar y subir nuevo'}
                                </Button>
                            </div>
                        )}
                    </div>
                );
            }
        }

        // CASO 2: Usuario NO ha subido video
        
        // Si hay ejemplo: mostrar ejemplo + uploader
        if (exampleVideo && exampleVideoUrl) {
            return (
                <div className="tab-content-video">
                    <div className="single-video-view">
                        <div className="example-video-container">
                            <span className="video-label-single">EJEMPLO</span>
                            <video
                                src={exampleVideoUrl}
                                controls
                                className="single-video"
                            />
                        </div>
                        {exampleVideo.title && (
                            <h4 className="example-title">{exampleVideo.title}</h4>
                        )}
                        {exampleVideo.description && (
                            <p className="example-description">{exampleVideo.description}</p>
                        )}
                        {exampleVideo.duration_seconds && (
                            <p className="example-duration">
                                ⏱️ Duración: {videoTypesService.formatDuration(exampleVideo.duration_seconds)}
                            </p>
                        )}
                    </div>

                    {examples.length > 1 && (
                        <div className="example-navigation">
                            <Button onClick={prevExample} variant="secondary" className="btn-nav">
                                ← Anterior
                            </Button>
                            <span className="example-counter">
                                {currentExampleIndex + 1} / {examples.length}
                            </span>
                            <Button onClick={nextExample} variant="secondary" className="btn-nav">
                                Siguiente →
                            </Button>
                        </div>
                    )}

                    {participantId && (
                        <CompactVideoUploader
                            videoType={videoType}
                            onFileSelect={handleFileSelect}
                            onUpload={handleUpload}
                            onCancel={handleCancelUpload}
                            isUploading={isUploading}
                            selectedFile={selectedFile}
                            previewUrl={previewUrl}
                            error={error}
                        />
                    )}
                </div>
            );
        }

        // Sin ejemplo: mostrar solo el uploader
        return (
            <div className="tab-content-video">
                {participantId ? (
                    <CompactVideoUploader
                        videoType={videoType}
                        onFileSelect={handleFileSelect}
                        onUpload={handleUpload}
                        onCancel={handleCancelUpload}
                        isUploading={isUploading}
                        selectedFile={selectedFile}
                        previewUrl={previewUrl}
                        error={error}
                    />
                ) : (
                    <div className="tab-content-empty">
                        <p>No hay videos de ejemplo disponibles.</p>
                    </div>
                )}
            </div>
        );
    };

    const renderDetailsTab = () => {
        return (
            <div className="tab-content-details">
                {videoType.description && (
                    <div className="details-section">
                        <h3>📝 Descripción</h3>
                        <p>{videoType.description}</p>
                    </div>
                )}

                {videoType.instructions && (
                    <div className="details-section">
                        <h3>📋 Instrucciones</h3>
                        <div className="instructions-text">{videoType.instructions}</div>
                    </div>
                )}

                <div className="details-section">
                    <h3>⚙️ Especificaciones Técnicas</h3>
                    <div className="specs-list">
                        <div className="spec-item">
                            <span className="spec-label">Formatos permitidos:</span>
                            <span className="spec-value">
                                {videoType.allowed_formats.map(f => f.toUpperCase()).join(', ')}
                            </span>
                        </div>
                        <div className="spec-item">
                            <span className="spec-label">Tamaño máximo:</span>
                            <span className="spec-value">{videoType.max_file_size_mb} MB</span>
                        </div>
                        <div className="spec-item">
                            <span className="spec-label">Duración máxima:</span>
                            <span className="spec-value">{videoType.max_duration_seconds} segundos</span>
                        </div>
                        <div className="spec-item">
                            <span className="spec-label">Resolución mínima:</span>
                            <span className="spec-value">{videoType.min_resolution}</span>
                        </div>
                        <div className="spec-item">
                            <span className="spec-label">Obligatorio:</span>
                            <span className={`spec-value ${videoType.is_required ? 'required' : 'optional'}`}>
                                {videoType.is_required ? '✓ Sí' : '✗ No'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderCriteriaTab = () => {
        if (criteria.length === 0) {
            return (
                <div className="tab-content-empty">
                    <p>No hay criterios de evaluación definidos para este video.</p>
                </div>
            );
        }

        return (
            <div className="tab-content-criteria">
                <div className="criteria-list">
                    {criteria.map((criterion) => (
                        <div key={criterion.id} className="criterion-card">
                            <div className="criterion-header">
                                <h4>{criterion.name}</h4>
                                <span className="criterion-weight">Peso: {criterion.weight}x</span>
                            </div>
                            {criterion.description && (
                                <p className="criterion-description">{criterion.description}</p>
                            )}
                            <div className="criterion-score">
                                <span>Puntuación: {criterion.min_score} - {criterion.max_score}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderRulesTab = () => {
        if (!categoryRules) {
            return (
                <div className="tab-content-empty">
                    <p>No hay reglamento disponible para esta categoría.</p>
                </div>
            );
        }

        return (
            <div className="tab-content-rules">
                <div className="rules-content">
                    <div dangerouslySetInnerHTML={{ __html: categoryRules }} />
                </div>
            </div>
        );
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-container modal-tabs video-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>🎥 {videoType.name}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div className="tabs-navigation">
                    <button
                        className={`tab-button ${activeTab === 'video' ? 'active' : ''}`}
                        onClick={() => setActiveTab('video')}
                    >
                        🎬 Video
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                        onClick={() => setActiveTab('details')}
                    >
                        📋 Detalles
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'criteria' ? 'active' : ''}`}
                        onClick={() => setActiveTab('criteria')}
                    >
                        ⭐ Criterios
                    </button>
                    {categoryRules && (
                        <button
                            className={`tab-button ${activeTab === 'rules' ? 'active' : ''}`}
                            onClick={() => setActiveTab('rules')}
                        >
                            📜 Reglamento
                        </button>
                    )}
                </div>

                <div className="modal-body">
                    {isLoading ? (
                        <Loading message="Cargando información..." />
                    ) : (
                        <>
                            {activeTab === 'video' && renderVideoTab()}
                            {activeTab === 'details' && renderDetailsTab()}
                            {activeTab === 'criteria' && renderCriteriaTab()}
                            {activeTab === 'rules' && renderRulesTab()}
                        </>
                    )}
                </div>

                <div className="modal-footer">
                    <Button onClick={onClose} variant="secondary">
                        Cerrar
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default VideoTypeDetailModal;